// src/components/forms/CustomSelect.jsx
import React, { useState, useRef, useEffect } from 'react';
import { IoChevronDownOutline, IoChevronUpOutline, IoSearchOutline } from 'react-icons/io5';

/**
 * A custom select component with search functionality
 *
 * NOTE:
 * - Logic/behavior kept the same.
 * - UI updated to AJMM Tailwind theme.
 * - Still emits onChange({ target: { name, value } }) exactly as before.
 */
const CustomSelect = ({
  options = [],
  value,
  onChange,
  placeholder = "Select an option",
  name,
  error,
  label,
  required,
  loading,
  loadingComponent,
  icon,
  searchable = true,
  searchPlaceholder = "Search options...",
  searchThreshold = 5
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState('bottom'); // 'bottom' or 'top'
  const selectRef = useRef(null);
  const searchInputRef = useRef(null);

  // Find the selected option label
  const selectedOption = options.find(option => option.value === value);

  // Filter options based on search query
  const filteredOptions = searchQuery.trim() === '' ? options : options.filter(option =>
    option.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus();
    }
    // Reset search query and highlighted index when dropdown closes
    if (!isOpen) {
      setSearchQuery('');
      setHighlightedIndex(0);
    }
  }, [isOpen, searchable]);

  // Keep highlighted option in view
  useEffect(() => {
    if (isOpen && highlightedIndex >= 0) {
      const dropdownEl = document.querySelector('.custom-select-dropdown');
      const highlightedEl = document.querySelector('.custom-select-option.highlighted');

      if (dropdownEl && highlightedEl) {
        const dropdownRect = dropdownEl.getBoundingClientRect();
        const highlightedRect = highlightedEl.getBoundingClientRect();

        const isAbove = highlightedRect.top < dropdownRect.top;
        const isBelow = highlightedRect.bottom > dropdownRect.bottom;

        if (isAbove || isBelow) {
          highlightedEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }
    }
  }, [highlightedIndex, isOpen]);

  // Determine dropdown position based on available space when opening
  useEffect(() => {
    if (isOpen && selectRef.current) {
      const selectRect = selectRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const spaceBelow = windowHeight - selectRect.bottom;
      const minSpaceNeeded = 200; // min space needed for dropdown (matching max-height)

      // If there's not enough space below, show dropdown above
      if (spaceBelow < minSpaceNeeded && selectRect.top > minSpaceNeeded) {
        setDropdownPosition('top');
      } else {
        setDropdownPosition('bottom');
      }
    }
  }, [isOpen]);

  const handleOptionClick = (option, e) => {
    // Stop the event from bubbling up to parent elements
    if (e) {
      e.stopPropagation();
    }

    onChange({ target: { name, value: option.value } });
    setIsOpen(false);
    setHighlightedIndex(options.findIndex(opt => opt.value === option.value));
    setSearchQuery('');
  };

  const toggleDropdown = () => {
    if (!loading) {
      setIsOpen(prev => !prev);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    // Reset highlighted index to first item when search changes
    setHighlightedIndex(0);
  };

  const handleSearchClick = (e) => {
    // Prevent dropdown from closing when clicking in the search input
    e.stopPropagation();
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (loading) return;

    // If search input is focused and dropdown is open, don't handle space or other typing keys
    if (
      isOpen &&
      document.activeElement === searchInputRef.current &&
      e.key !== "Enter" &&
      e.key !== "Escape" &&
      e.key !== "ArrowDown" &&
      e.key !== "ArrowUp"
    ) {
      return;
    }

    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (isOpen && filteredOptions.length > 0) {
        handleOptionClick(filteredOptions[highlightedIndex]);
      } else {
        setIsOpen(true);
      }
    } else if (e.key === "Escape") {
      setIsOpen(false);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (isOpen) {
        setHighlightedIndex(prev =>
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
      } else {
        setIsOpen(true);
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (isOpen) {
        setHighlightedIndex(prev =>
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
      } else {
        setIsOpen(true);
        setHighlightedIndex(filteredOptions.length - 1);
      }
    }
  };

  // Handle key events in search input
  const handleSearchInputKeyDown = (e) => {
    // Allow typing in the search field
    e.stopPropagation();

    // But still handle navigation keys
    if (e.key === "Escape") {
      setIsOpen(false);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (filteredOptions.length > 0) {
        setHighlightedIndex(prev =>
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (filteredOptions.length > 0) {
        setHighlightedIndex(prev =>
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
      }
    } else if (e.key === "Enter" && filteredOptions.length > 0) {
      e.preventDefault();
      handleOptionClick(filteredOptions[highlightedIndex]);
    } else if (e.key === "Tab") {
      // Let Tab work normally but close dropdown when tabbing out
      if (!e.shiftKey) {
        setTimeout(() => {
          if (!selectRef.current?.contains(document.activeElement)) {
            setIsOpen(false);
          }
        }, 0);
      }
    }
  };

  return (
    <div className="form-field custom-select-container">
      {label && (
        <label className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-white">
          {icon && (
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-blue-50 text-blue-700 ring-1 ring-blue-100 dark:bg-blue-950/40 dark:text-blue-200 dark:ring-blue-900/40">
              {icon}
            </span>
          )}
          <span className="inline-flex items-center gap-1">
            {label}
            {required && <span className="text-red-500">*</span>}
          </span>
        </label>
      )}

      <div
        ref={selectRef}
        className={[
          'custom-select',
          'relative',
          'w-full',
          'rounded-xl',
          'border',
          'bg-white',
          'px-4',
          'py-3',
          'text-sm',
          'font-medium',
          'text-slate-900',
          'shadow-sm',
          'transition',
          'outline-none',
          'focus:ring-2',
          'dark:bg-slate-900',
          'dark:text-white',
          isOpen ? 'border-blue-300 ring-blue-500/20 dark:border-blue-900/50 dark:ring-blue-500/20' : 'border-slate-200 dark:border-slate-800',
          error ? 'border-red-300 ring-red-500/20 dark:border-red-900/50 dark:ring-red-500/20' : '',
          loading ? 'cursor-not-allowed opacity-80' : 'cursor-pointer',
        ].filter(Boolean).join(' ')}
        onClick={toggleDropdown}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        role="combobox"
      >
        {loading ? (
          loadingComponent || (
            <div className="flex items-center justify-between gap-3">
              <div className="text-slate-600 dark:text-slate-300">Loading...</div>
              <div className="h-4 w-4 animate-pulse rounded-full bg-slate-200 dark:bg-slate-700" />
            </div>
          )
        ) : (
          <>
            <div className="custom-select-value pr-8">
              <span className={selectedOption ? 'text-slate-900 dark:text-white' : 'text-slate-500 dark:text-slate-400'}>
                {selectedOption ? selectedOption.label : placeholder}
              </span>
            </div>

            <div className="custom-select-icon absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-300">
              {isOpen ? <IoChevronUpOutline /> : <IoChevronDownOutline />}
            </div>

            {isOpen && (
              <div
                className={[
                  'custom-select-dropdown-container',
                  'absolute',
                  'left-0',
                  'z-50',
                  'w-full',
                  'overflow-hidden',
                  'rounded-2xl',
                  'border',
                  'border-slate-200',
                  'bg-white',
                  'shadow-xl',
                  'dark:border-slate-800',
                  'dark:bg-slate-900',
                  dropdownPosition === 'top' ? 'bottom-[calc(100%+8px)]' : 'top-[calc(100%+8px)]',
                ].join(' ')}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Search box */}
                {searchable && options.length >= searchThreshold && (
                  <div className="border-b border-slate-200 p-3 dark:border-slate-800">
                    <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-800 dark:bg-slate-950/40">
                      <div className="text-slate-500 dark:text-slate-300">
                        <IoSearchOutline />
                      </div>
                      <input
                        ref={searchInputRef}
                        type="text"
                        placeholder={searchPlaceholder}
                        value={searchQuery}
                        onChange={handleSearchChange}
                        onClick={handleSearchClick}
                        onKeyDown={handleSearchInputKeyDown}
                        className="w-full bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400 dark:text-white dark:placeholder:text-slate-500"
                        aria-label="Search options"
                      />
                    </div>
                  </div>
                )}

                {/* Options list */}
                <ul
                  className="custom-select-dropdown max-h-52 overflow-auto p-2"
                  role="listbox"
                >
                  {filteredOptions.length > 0 ? (
                    filteredOptions.map((option, index) => {
                      const isSelected = option.value === value;
                      const isHighlighted = index === highlightedIndex;

                      return (
                        <li
                          key={option.value}
                          className={[
                            'custom-select-option',
                            'flex',
                            'items-center',
                            'justify-between',
                            'gap-3',
                            'rounded-xl',
                            'px-3',
                            'py-2.5',
                            'text-sm',
                            'transition',
                            isHighlighted ? 'bg-blue-50 text-blue-800 dark:bg-blue-950/40 dark:text-blue-200' : 'text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800/60',
                            isSelected ? 'font-semibold' : 'font-medium',
                            isSelected ? 'selected' : '',
                            isHighlighted ? 'highlighted' : '',
                          ].join(' ')}
                          onClick={(e) => handleOptionClick(option, e)}
                          aria-selected={isSelected}
                          role="option"
                        >
                          <span className="min-w-0 truncate">{option.label}</span>
                          {isSelected ? (
                            <span className="text-xs font-semibold text-blue-700 dark:text-blue-200">
                              Selected
                            </span>
                          ) : null}
                        </li>
                      );
                    })
                  ) : (
                    <li className="custom-select-no-options rounded-xl px-3 py-2.5 text-sm text-slate-600 dark:text-slate-300">
                      No options found
                    </li>
                  )}
                </ul>
              </div>
            )}
          </>
        )}
      </div>

      {error && (
        <div className="mt-2 text-sm font-medium text-red-600">
          {error}
        </div>
      )}
    </div>
  );
};

export default CustomSelect;
